import { useState, useEffect } from 'react';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { Link } from 'wouter';

const LS_PROFILE = 'edison:setup-profile';
const LS_DEVICE = 'edison:setup-device';
const LS_PROJECT = 'edison:setup-project';
const LS_DISMISSED = 'edison:setup-checklist-dismissed';

interface SetupChecks {
  profile: boolean;
  device: boolean;
  project: boolean;
  dismissed: boolean;
}

function getChecks(): SetupChecks {
  return {
    profile: localStorage.getItem(LS_PROFILE) === 'true',
    device: localStorage.getItem(LS_DEVICE) === 'true',
    project: localStorage.getItem(LS_PROJECT) === 'true',
    dismissed: localStorage.getItem(LS_DISMISSED) === 'true',
  };
}

export function SetupChecklist() {
  const [checks, setChecks] = useState<SetupChecks>(getChecks());

  // Re-check on focus (in case user completed a step in another tab/page and came back)
  useEffect(() => {
    const refresh = () => setChecks(getChecks());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const allDone = checks.profile && checks.device && checks.project;

  if (checks.dismissed || allDone) return null;

  const handleDismiss = () => {
    localStorage.setItem(LS_DISMISSED, 'true');
    setChecks((prev) => ({ ...prev, dismissed: true }));
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 mb-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-indigo-900">Get started with Edison</h3>
          <p className="text-xs text-indigo-700 mt-0.5">
            Complete these steps to start capturing R&D evidence.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss setup checklist"
          className="text-indigo-400 hover:text-indigo-600 transition-colors ml-2 mt-0.5 flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2">
        <ChecklistItem
          done={checks.profile}
          label="Complete your profile"
          description="Paste your LinkedIn or CV to establish your R&D competence record"
          href="/profile"
        />
        <ChecklistItem
          done={checks.device}
          label="Register your development machine"
          description="Run the setup script to capture Claude Code sessions as R&D evidence"
          href="/integrations"
        />
        <ChecklistItem
          done={checks.project}
          label="Create your first project"
          description="Set up an R&D project and connect your GitHub repo"
          href="/projects"
        />
      </div>
    </div>
  );
}

interface ChecklistItemProps {
  done: boolean;
  label: string;
  description: string;
  href: string;
}

function ChecklistItem({ done, label, description, href }: ChecklistItemProps) {
  return (
    <div className="flex items-start gap-3">
      {done ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
      ) : (
        <Circle className="h-5 w-5 text-indigo-300 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        {done ? (
          <span className="text-sm font-medium text-indigo-500 line-through">{label}</span>
        ) : (
          <Link href={href} className="text-sm font-medium text-indigo-800 hover:text-indigo-600 hover:underline">
            {label} →
          </Link>
        )}
        <p className="text-xs text-indigo-600 mt-0.5">{description}</p>
      </div>
    </div>
  );
}
